import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ChangePasswordSection Component
 * 
 * Displays password management options based on auth provider:
 * - Local users: Can change password
 * - Google/OAuth users: Shows option to set a password for email login
 */
const ChangePasswordSection = ({ user, api }) => {
  const [isChanging, setIsChanging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const authProvider = user?.auth_provider || 'local';
  const hasLocalPassword = authProvider === 'local' || user?.has_local_password;

  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let score = 0;
    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) return { strength: 33, label: 'Weak', color: 'bg-red-500' };
    if (score <= 4) return { strength: 66, label: 'Medium', color: 'bg-yellow-500' };
    return { strength: 100, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength(formData.new_password);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (hasLocalPassword && !formData.current_password) {
      newErrors.current_password = 'Current password is required';
    }

    if (!formData.new_password) {
      newErrors.new_password = 'New password is required';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters';
    } else if (hasLocalPassword && formData.new_password === formData.current_password) {
      newErrors.new_password = 'New password must be different';
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = 'Please confirm your password';
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    try {
      await api.post('/users/change-password', {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      
      toast.success('Password changed successfully!');
      setIsChanging(false);
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Failed to change password';
      if (errorMessage.toLowerCase().includes('current password')) {
        setErrors(prev => ({ ...prev, current_password: errorMessage }));
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    if (!formData.new_password) {
      newErrors.new_password = 'Password is required';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters';
    }
    if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = 'Passwords do not match';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      await api.post('/users/set-local-password', {
        new_password: formData.new_password
      });
      
      toast.success('Password set successfully! You can now log in with email and password.');
      setIsChanging(false);
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  // OAuth user without local password
  if (!hasLocalPassword && !isChanging) {
    return (
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="font-heading">Password</CardTitle>
          <CardDescription>
            You signed up with Google. Set a password to also log in with email.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg mb-4">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Setting a password will allow you to log in with either Google or your email and password.
            </p>
          </div>
          <Button 
            variant="outline"
            onClick={() => setIsChanging(true)}
            data-testid="set-password-btn"
          >
            Set Email Password
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Form for setting password (OAuth users)
  if (!hasLocalPassword && isChanging) {
    return (
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="font-heading">Set Password</CardTitle>
          <CardDescription>
            Create a password for email login
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSetPassword} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="new_password"
                  name="new_password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={handleChange}
                  className={`pl-10 pr-10 ${errors.new_password ? 'border-red-500' : ''}`}
                  placeholder="Create a password"
                  data-testid="set-new-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.new_password && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.new_password}
                </p>
              )}
              
              {formData.new_password && (
                <div className="space-y-1">
                  <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                      style={{ width: `${passwordStrength.strength}%` }}
                    />
                  </div>
                  <p className={`text-xs ${
                    passwordStrength.label === 'Weak' ? 'text-red-500' :
                    passwordStrength.label === 'Medium' ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    Password strength: {passwordStrength.label}
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm_password">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="confirm_password"
                  name="confirm_password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.confirm_password}
                  onChange={handleChange}
                  className={`pl-10 pr-10 ${errors.confirm_password ? 'border-red-500' : ''}`}
                  placeholder="Confirm password"
                  data-testid="set-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirm_password && (
                <p className="text-red-500 text-sm flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  {errors.confirm_password}
                </p>
              )}
              {formData.confirm_password && formData.new_password === formData.confirm_password && !errors.confirm_password && (
                <p className="text-green-600 text-sm flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" />
                  Passwords match
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button 
                type="button" 
                variant="outline"
                onClick={() => {
                  setIsChanging(false);
                  setFormData({ current_password: '', new_password: '', confirm_password: '' });
                  setErrors({});
                }}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#004B8D] hover:bg-[#003865]"
                disabled={loading}
                data-testid="set-password-submit-btn"
              >
                {loading ? 'Setting...' : 'Set Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  // Local user - not changing
  if (!isChanging) {
    return (
      <Card className="border-slate-100">
        <CardHeader>
          <CardTitle className="font-heading">Password</CardTitle>
          <CardDescription>
            Update your password to keep your account secure
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button 
            variant="outline"
            onClick={() => setIsChanging(true)}
            data-testid="change-password-btn"
          >
            Change Password
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Local user - changing password
  return (
    <Card className="border-slate-100">
      <CardHeader>
        <CardTitle className="font-heading">Change Password</CardTitle>
        <CardDescription>
          Enter your current password and choose a new one
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current_password">Current Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="current_password"
                name="current_password"
                type={showCurrentPassword ? 'text' : 'password'}
                value={formData.current_password}
                onChange={handleChange}
                className={`pl-10 pr-10 ${errors.current_password ? 'border-red-500' : ''}`}
                placeholder="Enter current password"
                data-testid="current-password-input"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.current_password && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.current_password}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="new_password">New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="new_password"
                name="new_password"
                type={showNewPassword ? 'text' : 'password'}
                value={formData.new_password}
                onChange={handleChange}
                className={`pl-10 pr-10 ${errors.new_password ? 'border-red-500' : ''}`}
                placeholder="Enter new password"
                data-testid="new-password-input"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.new_password && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.new_password}
              </p>
            )}
            
            {formData.new_password && (
              <div className="space-y-1">
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-300 ${passwordStrength.color}`}
                    style={{ width: `${passwordStrength.strength}%` }}
                  />
                </div>
                <p className={`text-xs ${
                  passwordStrength.label === 'Weak' ? 'text-red-500' :
                  passwordStrength.label === 'Medium' ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  Password strength: {passwordStrength.label}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirm_password">Confirm New Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="confirm_password"
                name="confirm_password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={handleChange}
                className={`pl-10 pr-10 ${errors.confirm_password ? 'border-red-500' : ''}`}
                placeholder="Confirm new password"
                data-testid="confirm-password-input"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {errors.confirm_password && (
              <p className="text-red-500 text-sm flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.confirm_password}
              </p>
            )}
            {formData.confirm_password && formData.new_password === formData.confirm_password && !errors.confirm_password && (
              <p className="text-green-600 text-sm flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Passwords match
              </p>
            )}
          </div>

          <div className="flex gap-2">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => {
                setIsChanging(false);
                setFormData({ current_password: '', new_password: '', confirm_password: '' });
                setErrors({});
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-[#004B8D] hover:bg-[#003865]"
              disabled={loading}
              data-testid="change-password-submit-btn"
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default ChangePasswordSection;
